import { MARQUEE_GRADIENT_WIDTH, MARQUEE_SPEED } from './constants'
import { formatMediaTime } from 'd2/utils/dates'
import { memo } from 'react'
import Audio from 'd2/components/AudioPlayer/Audio'
import BlankButton from 'd2/components/ButtonV2/BlankButton'
import Body from 'd2/components/Typography/Body'
import BodySmall from 'd2/components/Typography/BodySmall'
import Marquee from 'react-fast-marquee'
import ProgressBar from 'd2/components/AudioPlayer/ProgressBar'
import SpaceBetween from 'd2/components/Layout/SpaceBetween'
import VolumeButton from 'd2/components/AudioPlayer/VolumeButton'
import useAudioPlayer from 'd2/hooks/useAudioPlayer'
import useStyles from './styles'
import useTranslations from 'd2/components/AudioPlayer/translations'
import type { Props } from './types'

const DisconnectedAudioPlayer = memo<Props>(({
  autoPlay,
  autoPlayOnTrackChange,
  currentTime,
  hidden,
  loop,
  muted,
  name,
  onDurationChange,
  onPause,
  onPlay,
  playing,
  preload,
  setCurrentTime,
  setPlaying,
  src,
  togglePlaying,
}) => {
  const t = useTranslations()
  const { classes } = useStyles()
  const {
    duration,
    setDuration,
    setVolume,
    volume,
  } = useAudioPlayer()

  return (
    <>
      <Audio
        autoPlay={autoPlay}
        autoPlayOnTrackChange={autoPlayOnTrackChange}
        currentTime={currentTime}
        duration={duration}
        loop={loop}
        muted={muted}
        onDurationChange={onDurationChange}
        onPause={onPause}
        onPlay={onPlay}
        playing={playing}
        preload={preload}
        setCurrentTime={setCurrentTime}
        setDuration={setDuration}
        setPlaying={setPlaying}
        src={src}
        volume={volume}
      />
      { !hidden && (<>
        <SpaceBetween className={classes.audioPlayer}>
          <div className={classes.iconButtonWrapper}>
            <BlankButton
              className={classes.iconButton}
              icon={playing ? 'pause' : 'play'}
              noPadding
              onClick={togglePlaying}
            />
          </div>
          <Marquee
            gradientWidth={MARQUEE_GRADIENT_WIDTH}
            speed={MARQUEE_SPEED}
          >
            <div>
              <Body variant='italic'>
                { t.nowPlaying }
              </Body>
              <Body className={classes.truncate}>
                { /* NOTE: we need a leading space and interpolation will not work
                          because HTML trims leading white space. */ }
                  &nbsp;
                { name ?? '' }
              </Body>
            </div>
          </Marquee>
          <BodySmall
            color='gray'
            variant='italic'
          >
            { formatMediaTime(currentTime) }
          </BodySmall>
          <div className={classes.volumeButtonWrapper}>
            <VolumeButton
              setVolume={setVolume}
              volume={volume}
            />
          </div>
        </SpaceBetween>
        <div className={classes.progressBar}>
          <ProgressBar
            currentTime={currentTime}
            duration={duration}
            setCurrentTime={setCurrentTime}
          />
        </div>
      </>) }
    </>
  )
})

DisconnectedAudioPlayer.displayName = 'DisconnectedAudioPlayer'

export default DisconnectedAudioPlayer
